import React, { useState, useEffect } from "react";

const LeagueClubsModal = ({ league, clubs, onClose, onAddClubs, onRemoveClubs }) => {
  const [selectedClubs, setSelectedClubs] = useState([]);
  const [leagueClubIds, setLeagueClubIds] = useState([]);
  const [availableClubs, setAvailableClubs] = useState([]);

  useEffect(() => {
    const currentClubIds = (league.clubs || []).map((c) => c.clubId);
    setLeagueClubIds(currentClubIds);
    const available = clubs.filter(
      (club) => !currentClubIds.includes(club.clubId)
    );
    setAvailableClubs(available);
  }, [league, clubs]);

  const handleAddClubs = () => {
    const clubIds = selectedClubs.map(id => parseInt(id));
    onAddClubs(clubIds);
    setSelectedClubs([]);
  };

  const handleRemoveClubs = () => {
    const clubIds = selectedClubs.map(id => parseInt(id));
    onRemoveClubs(clubIds);
    setSelectedClubs([]);
  };

  // Helper for card style
  const cardClass =
    "bg-surface-900 border border-line rounded-xl p-4 mb-2 flex items-center transition hover:border-line-strong focus-within:ring-2 focus-within:ring-accent";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface-900 rounded-2xl shadow-2xl w-full max-w-3xl p-8 relative animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none"
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="text-2xl font-bold mb-6 text-white tracking-tight flex items-center gap-2">
          <span className="inline-block w-2 h-6 bg-accent rounded-full mr-2"></span>
          Manage Clubs <span className="text-accent-soft">- {league.name}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Available Clubs Section */}
          <div>
            <h3 className="text-lg font-semibold text-accent-soft mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-4 bg-accent rounded-full"></span>
              Available Clubs
            </h3>
            <div className="max-h-64 overflow-y-auto pr-2">
              {availableClubs.length === 0 && (
                <div className="text-gray-400 italic text-sm py-4 text-center">No clubs available to add.</div>
              )}
              {availableClubs.map((club) => (
                <label
                  key={club.clubId}
                  htmlFor={`add-${club.clubId}`}
                  className={cardClass + (selectedClubs.includes(club.clubId.toString()) ? " ring-2 ring-accent" : "")}
                  tabIndex={0}
                >
                  <input
                    type="checkbox"
                    id={`add-${club.clubId}`}
                    value={club.clubId}
                    checked={selectedClubs.includes(club.clubId.toString())}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedClubs(prev =>
                        e.target.checked
                          ? [...prev, value]
                          : prev.filter(id => id !== value)
                      );
                    }}
                    className="mr-3 accent-accent scale-125 focus:ring-2 focus:ring-accent"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-white">{club.name}</div>
                    <div className="text-xs text-gray-400">{club.location} &middot; Founded {club.foundedYear}</div>
                  </div>
                  {club.logo && (
                    <img src={club.logo} alt={club.name} className="w-10 h-10 object-cover rounded-full ml-4 border border-white/20" />
                  )}
                </label>
              ))}
            </div>
            <button
              onClick={handleAddClubs}
              disabled={!selectedClubs.length}
              className={`mt-4 w-full py-2 rounded-lg font-semibold transition text-white ${
                selectedClubs.length
                  ? "bg-accent hover:bg-accent-hover shadow-md"
                  : "bg-surface-700 cursor-not-allowed"
              }`}
            >
              <span className="inline-block mr-2">+</span> Add Selected Clubs
            </button>
          </div>

          {/* Current Clubs Section */}
          <div>
            <h3 className="text-lg font-semibold text-accent-soft mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-4 bg-accent rounded-full"></span>
              Current Clubs
            </h3>
            <div className="max-h-64 overflow-y-auto pr-2">
              {(league.clubs || []).length === 0 && (
                <div className="text-gray-400 italic text-sm py-4 text-center">No clubs in this league yet.</div>
              )}
              {(league.clubs || []).map((club) => (
                <label
                  key={club.clubId}
                  htmlFor={`remove-${club.clubId}`}
                  className={cardClass + (selectedClubs.includes(club.clubId.toString()) ? " ring-2 ring-pink-400" : "")}
                  tabIndex={0}
                >
                  <input
                    type="checkbox"
                    id={`remove-${club.clubId}`}
                    value={club.clubId}
                    checked={selectedClubs.includes(club.clubId.toString())}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedClubs(prev =>
                        e.target.checked
                          ? [...prev, value]
                          : prev.filter(id => id !== value)
                      );
                    }}
                    className="mr-3 accent-pink-500 scale-125 focus:ring-2 focus:ring-pink-400"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-white">{club.name}</div>
                    <div className="text-xs text-gray-400">{club.location} &middot; Founded {club.foundedYear}</div>
                  </div>
                  {club.logo && (
                    <img src={club.logo} alt={club.name} className="w-10 h-10 object-cover rounded-full ml-4 border border-white/20" />
                  )}
                </label>
              ))}
            </div>
            <button
              onClick={handleRemoveClubs}
              disabled={!selectedClubs.length}
              className={`mt-4 w-full py-2 rounded-lg font-semibold transition text-white ${
                selectedClubs.length
                  ? "bg-pink-600 hover:bg-pink-700 shadow-md"
                  : "bg-surface-700 cursor-not-allowed"
              }`}
            >
              <span className="inline-block mr-2">&minus;</span> Remove Selected Clubs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueClubsModal; 